import React, { useState, useEffect } from "react";
import { Box, Button, FormField, Text, Heading, Tip } from "grommet";
import { Info, Trash, Indicator } from "grommet-icons";
import Select from "react-select";

const ProductsSection = ({
  size,
  formData,
  setFormData,
  products,
  setProducts,
  editIndex,
  setEditIndex,
  productSkills,
  subProductSkills,
  skillCoursesById = {},
  handleDeleteProduct,
}) => {
  // State for selected option
  const [selectedProductOption, setSelectedProductOption] = useState(null);
  const [selectedProductLevel, setSelectedProductLevel] = useState(null);
  const [selectedCourseOptions, setSelectedCourseOptions] = useState([]);
  const [courseSelectionTouched, setCourseSelectionTouched] = useState(false);
  // URL path format /add-proficiency/114, extract user ID
  const userIdFromPath = window.location.pathname.split("/")[2];

  const userIdsToFilterCourses = [84, 114]; // only these users can see restricted skills
  const isUserIdInFilterList = userIdsToFilterCourses.includes(
    parseInt(userIdFromPath, 10),
  );

  // These subProduct IDs are restricted: only visible to users in userIdsToFilterCourses
  const restrictedSubProductIds = [128, 129, 130];
  const aiFactorySkillIds = [128, 129, 130];

  // Prepare options for the dropdown
  const options = productSkills.flatMap((product) =>
    subProductSkills
      .filter((subProduct) => subProduct.product_id === product.id)
      .filter((subProduct) => {
        const isRestricted = restrictedSubProductIds.includes(subProduct.id);

        if (isRestricted) {
          // Restricted skills: show only if user is in the list
          return isUserIdInFilterList;
        }

        // Non-restricted skills: show to everyone
        return true;
      })
      .map((subProduct) => ({
        label: `${product.name} - ${subProduct.skill_name}`,
        value: {
          productName: product.name,
          productId: product.id,
          subProductName: subProduct.skill_name,
          subProductId: subProduct.id,
          is_only_foundation: subProduct.is_only_foundation,
        },
      })),
  );

  const selectedSkillId = selectedProductOption?.value?.subProductId;
  const isAiFactorySkill = aiFactorySkillIds.includes(Number(selectedSkillId));
  const selectedSkillCourses = isAiFactorySkill
    ? skillCoursesById[selectedSkillId] || []
    : [];
  
  // Get all added course IDs across all skills
  const allAddedCourseIds = products.flatMap(
    (product) => product.courseIds || []
  );
  
  // Filter out courses that have already been added to any skill
  const courseOptions = selectedSkillCourses
    .filter((course) => !allAddedCourseIds.includes(course.course_id))
    .map((course) => ({
      label: course.course_name,
      value: course.course_id,
    }));

  useEffect(() => {
    if (!selectedProductOption) {
      return;
    }
    if (!isAiFactorySkill || courseSelectionTouched) {
      return;
    }
    const courses = skillCoursesById[selectedSkillId] || [];
    if (courses.length === 0) {
      return;
    }
    
    // Get all added course IDs across all skills
    const allAddedCourseIds = products.flatMap(
      (product) => product.courseIds || []
    );
    
    // Filter out courses that have already been added to any skill
    const availableCourses = courses
      .filter((course) => !allAddedCourseIds.includes(course.course_id))
      .map((course) => ({
        label: course.course_name,
        value: course.course_id,
      }));
    
    setSelectedCourseOptions(availableCourses);
  }, [
    courseSelectionTouched,
    isAiFactorySkill,
    selectedProductOption,
    selectedSkillId,
    skillCoursesById,
    products,
  ]);

  const handleAddProduct = () => {
    const { productExperienceYears } = formData;
    if (products.length >= 6) {
      alert("You can only select up to 6 skills.");
      return;
    }
    if (!selectedProductOption) {
      alert("Please select a product and sub-category.");
      return;
    }

    const { productName, productId, subProductName, subProductId } =
      selectedProductOption.value;
    const selectedCourseIds = selectedCourseOptions.map(
      (option) => option.value,
    );
    const selectedCourseNames = selectedCourseOptions.map(
      (option) => option.label,
    );

    if (isAiFactorySkill && courseOptions.length > 0) {
      if (selectedCourseIds.length === 0) {
        alert("Please select at least one course for this skill.");
        return;
      }
    }

    const isProductExists = products.some(
      (product) =>
        product.productName === productName &&
        product.subProductName === subProductName,
    );

    if (isProductExists) {
      alert("This product has already been added.");
      return;
    }

    const newProduct = {
      id: productId,
      productName,
      productLevel: selectedProductLevel?.value,
      productExperienceYears,
      subProductSkillId: subProductId,
      subProductName,
      ...(selectedCourseIds.length > 0 && {
        courseIds: selectedCourseIds,
        courseNames: selectedCourseNames,
      }),
    };

    if (editIndex !== null) {
      const updatedProducts = [...products];
      updatedProducts[editIndex] = newProduct;
      setProducts(updatedProducts);
      setEditIndex(null);
    } else {
      setProducts([...products, newProduct]);
    }

    setFormData({
      ...formData,
      productLevel: "",
      productExperienceYears: "",
    });
    setSelectedProductOption(null);
    setSelectedProductLevel(null);
    setSelectedCourseOptions([]);
    setCourseSelectionTouched(false);
  };

  // Custom styles for react-select components
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      width: "100%",
      height: "25px",
      borderColor: state.isFocused ? "#01a982" : provided.borderColor,
      boxShadow: state.isFocused ? "0 0 0 2px #01a982" : provided.boxShadow,
      "&:hover": {
        borderColor: "#01a982",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#01a982" : provided.backgroundColor,
      color: state.isFocused ? "#fff" : provided.color,
      padding: "10px 20px", // Adjusted for better readability
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 5,
    }),
  };
  const courseSelectStyles = {
    ...customSelectStyles,
    control: (provided, state) => ({
      ...customSelectStyles.control(provided, state),
      height: "auto",
      minHeight: "32px",
      alignItems: "flex-start",
    }),
    valueContainer: (provided) => ({
      ...provided,
      maxHeight: "80px",
      overflowY: "auto",
      flexWrap: "wrap",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "auto",
    }),
  };
  const baseProductOptions = [
    { label: "Beginner", value: 1 },
    { label: "Intermediate", value: 2 },
    { label: "Advanced", value: 3 },
  ];

  const productOptions =
    selectedProductOption?.value?.is_only_foundation === 1
      ? baseProductOptions.map((option) =>
        option.value === 1 ? option : { ...option, isDisabled: true },
      )
      : baseProductOptions;
  const isAddDisabled =
    !selectedProductOption ||
    !selectedProductLevel ||
    (isAiFactorySkill &&
      courseOptions.length > 0 &&
      selectedCourseOptions.length === 0);

  return (
    <Box>
      {/* Header and Two Columns for Intro and Steps */}
      <Box align="center" direction="column" fill="horizontal">
        <Heading level="2" size="large">
          Welcome to HPE Digital Skill Advisor
        </Heading>
      </Box>

      {/* Wrap both sections in a parent row container */}
      <Box
        direction="row"
        gap="large"
        justify="between"
        width="xlarge"
        margin={{ vertical: "medium" }}
      >
        {/* Left Box with introductory text */}
        <Box
          background="light-2" // Light background to differentiate the section
          pad="medium" // Padding for a more spaced-out appearance
          round="small" // Rounded edges for better aesthetics
          width="large" // Increased width for better spacing
        >
          <Text size="medium" color="dark-3">
            In just a few minutes, the HPE Digital Skill Advisor will walk you
            through a set of questions designed to assess your proficiency in
            various technical solutions and topics. It will help determine your
            current skill level and provide a personalized learning
            recommendation based on your responses.
          </Text>
          <Text
            size="medium"
            color="dark-3"
            alignSelf="left"
            margin={{ top: "small" }}
          >
            To get started, select at least 1 and at most 6 skills for the
            assessment. Once you are ready, please click on{" "}
            <b>‘Proceed to skills check’</b> to get started.
          </Text>
          <Box
            direction="row"
            gap="small"
            align="center"
            margin={{ top: "small" }}
          >
            <Indicator color="brand" />
            <Text size="medium" color="dark-1">
              You can retake the assessment as many times as needed.
            </Text>
          </Box>
        </Box>

        {/* Right Box with step-by-step instructions */}
        <Box
          background="light-2" // Light background to differentiate the section
          pad="medium" // Padding for a more spaced-out appearance
          round="small" // Rounded edges for better aesthetics
          width="large" // Increased width for better spacing
        >
          {/* Step-by-step instructions with icons */}
          <Box direction="row" gap="small" align="center">
            <Info color="brand" />
            <Text size="medium" color="dark-1">
              <strong>Step 1:</strong> Select the skill area you are looking to
              develop.
            </Text>
          </Box>

          <Box
            direction="row"
            gap="small"
            align="center"
            margin={{ top: "small" }}
          >
            <Box direction="row" align="start" gap="xsmall">
              <Info color="brand" />
              <Text size="medium" color="dark-1">
                <strong>Step 2:</strong> Choose your level of proficiency in the
                skill you have selected—
                <br />
                {"  "}
                <strong>Beginner:</strong> Choose Beginner if you have less than
                2 years of experience with the product.
                <br />
                {"  "}
                <strong>Intermediate:</strong> Choose Intermediate if you have
                more than 2 years or less than 5 years of experience.
                <br />
                {"  "}
                <strong>Advanced:</strong> Choose Advanced if you have more than
                5 years of experience and extensive knowledge of the product.
              </Text>
            </Box>
          </Box>

          <Box
            direction="row"
            gap="small"
            align="center"
            margin={{ top: "small" }}
          >
            <Info color="brand" />
            <Text size="medium" color="dark-1">
              <strong>Step 3:</strong> Once you’ve made your selections, click{" "}
              <strong>‘Add’</strong>. Repeat the process to add up to{" "}
              <strong>6 skills</strong> for analysis.
            </Text>
          </Box>
        </Box>
      </Box>

      {/* Product Based Skills Section */}
      <Box direction="row" align="center" margin={{ top: "medium" }}>
        <Heading level="3" size="large">
          Product based skills
        </Heading>
        <Box pad={{ horizontal: "small" }}>
          <Tip content="Select product based skills">
            <Info color="brand" size="small" />
          </Tip>
        </Box>
      </Box>

      <Box
        border={{ color: "brand", size: "small" }}
        margin={{ bottom: "small" }}
      />

      {/* Form Fields */}
      <Box direction="column" gap="small">
        <Box
          direction={size === "small" ? "column" : "row"}
          gap="medium"
          align="center"
          width="xlarge"
        >
          {/* Single-select Dropdown with Search */}
          <FormField name="productSubCategory" style={{ flex: 1 }}>
            <Select
              options={options}
              value={selectedProductOption}
              placeholder="Select skill"
              onChange={(option) => {
                setSelectedProductOption(option);
                setSelectedProductLevel(null);
                setCourseSelectionTouched(false);
                const skillId = option?.value?.subProductId;
                if (aiFactorySkillIds.includes(Number(skillId))) {
                  const courses = skillCoursesById[skillId] || [];
                  
                  // Get all added course IDs across all skills
                  const allAddedCourseIds = products.flatMap(
                    (product) => product.courseIds || []
                  );
                  
                  // Filter out courses that have already been added to any skill
                  const availableCourses = courses
                    .filter((course) => !allAddedCourseIds.includes(course.course_id))
                    .map((course) => ({
                      label: course.course_name,
                      value: course.course_id,
                    }));
                  
                  setSelectedCourseOptions(availableCourses);
                } else {
                  setSelectedCourseOptions([]);
                }
              }}
              isSearchable
              styles={customSelectStyles}
            />
          </FormField>

          {/* Level Select */}
          <FormField name="productLevel" style={{ flex: 1 }}>
            <Select
              options={productOptions}
              value={selectedProductLevel}
              placeholder="Select your skill level"
              onChange={(option) => setSelectedProductLevel(option)}
              isSearchable={false}
              styles={customSelectStyles}
            />
          </FormField>

          {/* Add/Update Button */}
          <Button
            primary
            label={"Add"}
            onClick={handleAddProduct}
            disabled={isAddDisabled}
            style={{ width: "100px" }}
          />
        </Box>

        {selectedProductOption && isAiFactorySkill && selectedProductLevel && (
          <Box width="xlarge">
            <FormField name="productCourses" label="Availiable skills">
              {courseOptions.length > 0 ? (
                <Select
                  options={courseOptions}
                  value={selectedCourseOptions}
                  placeholder="Select course(s)"
                  onChange={(options) => {
                    setSelectedCourseOptions(options || []);
                    setCourseSelectionTouched(true);
                  }}
                  isMulti
                  closeMenuOnSelect={false}
                  styles={courseSelectStyles}
                />
              ) : (
                <Text size="small" color="dark-5">
                  No courses available for this skill.
                </Text>
              )}
            </FormField>
          </Box>
        )}

        {/* Display Added Products */}
        <Box margin={{ top: "small" }}>
          <Text size="medium" color="grey">
            Selected skill(s):
          </Text>
          {products.map((product, index) => (
            <Box
              key={index}
              direction={size === "small" ? "column" : "row"}
              justify="between"
              align="center"
              background="light-2"
              pad={{ vertical: "small", horizontal: "medium" }}
              round="small"
              margin={{ vertical: "xsmall" }}
            >
              <Box>
                <Text>
                  {`${product.productName} - ${product.subProductName}`}
                  {product.productLevel &&
                    ` - ${product.productLevel === 1
                      ? "Beginner"
                      : product.productLevel === 2
                        ? "Intermediate"
                        : product.productLevel === 3
                          ? "Advanced"
                          : "Unknown"
                    }`}
                </Text>
                {product.courseNames?.length > 0 && (
                  <Text size="small" color="dark-5">
                    Courses: {product.courseNames.join(", ")}
                  </Text>
                )}
              </Box>
              <Box direction="row" gap="small">
                <Button
                  icon={<Trash />}
                  onClick={() => handleDeleteProduct(index)}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ProductsSection;
